import React, { ReactNode, useMemo } from 'react'
import { Flex, Box, Image, Text } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Clock } from 'react-feather'
import qs from 'query-string'
import { BatchSubmissionState, PartiposterProfile } from 'state/submissions'
import { theme } from 'theme'
import { AccountTags, SubmissionTags } from 'components/Tag'
import { parseBackendDate } from 'services/dates'
import { selectSelectedCampaignSummary, selectSearchablePlatforms } from 'state/review'
import { selectFollowerTierOptions } from 'state/constants'
import SocialMediaFollowerTier from './common/SocialMediaFollowerTier'
import { RequirementState, REQUIREMENT_TYPE_ENUM, CampaignSummaryState } from 'state/campaigns'
import { selectPartiposterProfileById } from 'state/partiposter'
import { Country, FollowerTierOption, PLATFORM_ENUM } from 'services/api/constants'
import { PLATFORM_DISPLAY_NAME, PLATFORM_REDIRECT_URL } from 'services/numbers'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { spacing } from 'particles/styles'
import ParticleText from 'particles/Text'
import styled from 'styled-components'
import { SearchParamObject } from 'components/utils/url'
import { coreColors } from 'particles/colors'
import { State } from 'state'

interface JoinedAtDisplayProps {
    date: string | undefined
    country: Country
}

export const JoinedAtDisplay: React.FC<JoinedAtDisplayProps> = ({ date, country }) => {
    const [newT] = useNewTranslation()
    return date ? (
        <Box>
            <Text fontSize="0.7em" pt={theme.spacing.xxs} textAlign="left" color={theme.colors.doveGrey}>
                <Clock size={10} stroke={theme.colors.doveGrey} />{' '}
                {newT('card.submission.joinedCampaignDate', {
                    defaultValue: 'Joined campaign on {{submittedDate}}',
                    submittedDate: parseBackendDate(date, country, { showTime: true }),
                })}
            </Text>
        </Box>
    ) : null
}

interface PlatformLinkItemProps {
    platform: PLATFORM_ENUM
    username: string
}

const AnchorLink = styled.a`
    text-decoration: none;
`

export const PlatformLinkItem: React.FC<PlatformLinkItemProps> = ({ platform, username }) => (
    <Box width={'125px'}>
        {username ? (
            <AnchorLink
                href={`${PLATFORM_REDIRECT_URL[Number(platform) as PLATFORM_ENUM]}${username}/`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <ParticleText
                    scale="caption400"
                    color="secondary"
                    style={{
                        width: '125px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textAlign: 'left',
                    }}
                >
                    {PLATFORM_DISPLAY_NAME[Number(platform) as PLATFORM_ENUM]}: {'@' + username}
                </ParticleText>
            </AnchorLink>
        ) : (
            <ParticleText
                scale="caption400"
                style={{
                    width: '125px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                }}
            >
                {PLATFORM_DISPLAY_NAME[Number(platform) as PLATFORM_ENUM]}: N/A
            </ParticleText>
        )}
    </Box>
)

interface PlaformLinkRowProps {
    platforms: PLATFORM_ENUM[]
    usernames: string[]
}

export const PlatformLinkRow: React.FC<PlaformLinkRowProps> = ({ platforms, usernames }) => {
    return (
        <Flex mb="3px">
            {platforms && platforms.length > 0 && <PlatformLinkItem platform={platforms[0]} username={usernames[0]} />}
            <Box mr={theme.newSpacing.m} />
            {platforms && platforms.length > 1 && <PlatformLinkItem platform={platforms[1]} username={usernames[1]} />}
        </Flex>
    )
}

interface ProfileOptions {
    instagram: boolean
    facebook: boolean
    joinedAt?: boolean
}

interface LayoutProps {
    campaignSummary: CampaignSummaryState | null
    campaignUuid: string
    canManageTag: boolean
    followerRequirements?: RequirementState
    followerTierOptions: FollowerTierOption[]
    JoinedAt: ReactNode
    profile: PartiposterProfile
    SearchablePlatforms: ReactNode
    submissionUuid: string
    TopRightComponent?: ReactNode
}

// TODO: Replace rebass component for upcoming revamp in review layout view
const ReviewLayout: React.FC<LayoutProps> = ({
    campaignSummary,
    campaignUuid,
    canManageTag,
    followerRequirements,
    followerTierOptions,
    JoinedAt,
    profile,
    SearchablePlatforms,
    submissionUuid,
    TopRightComponent,
}) => {
    return (
        <>
            <Flex alignItems="center" mb={theme.spacing.xxxs}>
                <Box mb={'auto'} mt="5px">
                    <Image width="52px" height="52px" src={profile.photo} variant="avatar" />
                </Box>

                <Box flex={1} ml={theme.spacing.xxxs}>
                    <Flex>
                        <Box mt="5px">
                            <Text textAlign="left" fontWeight="bold" fontSize="1em">
                                {profile.name}, {profile.age}
                            </Text>
                        </Box>
                        <Box mx="auto" />
                        {TopRightComponent}
                    </Flex>
                    <Box mt={theme.newSpacing.xxxxs}>{SearchablePlatforms}</Box>
                    {canManageTag && (
                        <Box my={theme.newSpacing.xxxs}>
                            <AccountTags
                                profileData={profile}
                                submissionUuid={submissionUuid}
                                types={{ influencer: true }}
                            />
                        </Box>
                    )}
                </Box>
            </Flex>
            <Text fontSize="0.8em" textAlign="left">
                <Flex>
                    {campaignSummary && profile && followerTierOptions && followerRequirements && (
                        <Box my={spacing[300]}>
                            <SocialMediaFollowerTier
                                social_media_accounts={profile.social_media_accounts}
                                followerRequirement={followerRequirements}
                            />
                        </Box>
                    )}
                </Flex>
            </Text>

            {JoinedAt}

            {canManageTag && (
                <>
                    <Box my={theme.newSpacing.xxxs}>
                        <AccountTags
                            profileData={profile}
                            submissionUuid={submissionUuid}
                            types={{ photo: true, caption: true }}
                        />
                    </Box>
                    <SubmissionTags profileData={profile} submissionUuid={submissionUuid} campaignUuid={campaignUuid} />
                </>
            )}
        </>
    )
}

// TODO: replace rebass component
const VerifyLayout: React.FC<LayoutProps> = ({
    campaignSummary,
    campaignUuid,
    canManageTag,
    followerRequirements,
    followerTierOptions,
    JoinedAt,
    profile,
    SearchablePlatforms,
    submissionUuid,
    TopRightComponent,
}) => {
    return (
        <>
            <Flex alignItems="center" sx={{ gap: '6px' }}>
                <Box mb="auto">
                    <Image
                        width="52px"
                        height="52px"
                        sx={{ borderRadius: '2px' }}
                        src={profile.photo}
                        variant="avatar"
                    />
                </Box>

                <Flex flex={1} flexDirection="column" sx={{ gap: spacing[200] }}>
                    <Flex alignItems="center" sx={{ gap: spacing[200] }}>
                        <Box>
                            <ParticleText textAlign="left" scale="bodyM700">
                                {profile.name}, {profile.age}
                            </ParticleText>
                        </Box>

                        <Box sx={{ borderLeft: '1px solid', borderColor: coreColors.grey200, height: '24px' }} />

                        <Box>{SearchablePlatforms}</Box>
                        <Box mx="auto" />
                        {TopRightComponent}
                    </Flex>

                    {campaignSummary && profile && followerTierOptions && followerRequirements && (
                        <SocialMediaFollowerTier
                            social_media_accounts={profile.social_media_accounts}
                            followerRequirement={followerRequirements}
                        />
                    )}
                    {canManageTag && (
                        <>
                            <Flex sx={{ gap: spacing[200] }}>
                                <AccountTags
                                    profileData={profile}
                                    submissionUuid={submissionUuid}
                                    types={{ influencer: true }}
                                />
                                <AccountTags
                                    profileData={profile}
                                    submissionUuid={submissionUuid}
                                    types={{ photo: true, caption: true }}
                                />
                            </Flex>
                            <SubmissionTags
                                profileData={profile}
                                submissionUuid={submissionUuid}
                                campaignUuid={campaignUuid}
                            />
                        </>
                    )}
                </Flex>
            </Flex>

            {JoinedAt}
        </>
    )
}

export interface PartiposterProfileProps {
    isVerifyMode?: boolean
    options?: ProfileOptions
    submission: BatchSubmissionState
    submissionUuid: string
    TopRightComponent?: ReactNode
}

const Profile: React.FC<PartiposterProfileProps> = ({
    isVerifyMode,
    options = { instagram: true, facebook: false, joinedAt: false },
    submission,
    submissionUuid,
    TopRightComponent,
}) => {
    const location = useLocation()
    const { campaign } = qs.parse(location.search) as SearchParamObject
    const campaignUuid: string = campaign as string
    const profile = useSelector(selectPartiposterProfileById(submission.partiposterId))
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const followerTierOptions = useSelector(selectFollowerTierOptions)
    const canManageTag = usePermission(CampaignSubmission.ManageTag)
    const campaignWithRequirements = useSelector((state: State) =>
        campaignUuid ? state.campaigns.byId.campaigns[campaignUuid as string] : undefined,
    )
    const searchablePlatforms = useSelector(selectSearchablePlatforms)

    const platformUsernames = searchablePlatforms.map(platform => {
        const account = profile.social_media_accounts.filter(account => {
            return account.platform === platform
        })
        return account?.[0]?.username ?? ''
    })

    const followerRequirements = campaignWithRequirements?.requirements.find(
        (requirement: RequirementState) => requirement.type == REQUIREMENT_TYPE_ENUM.FOLLOWER,
    )

    const SearchablePlatforms = useMemo(() => {
        return searchablePlatforms?.map((_, index) => {
            if (index % 2 === 0) {
                return (
                    <PlatformLinkRow
                        key={index}
                        platforms={searchablePlatforms.slice(index)}
                        usernames={platformUsernames.slice(index)}
                    />
                )
            } else {
                return null
            }
        })
    }, [searchablePlatforms])

    const JoinedAt = options.joinedAt && campaignSummary && (
        <JoinedAtDisplay date={submission.first_content_submitted_at} country={campaignSummary.country} />
    )

    const layoutProps: LayoutProps = {
        campaignSummary,
        campaignUuid,
        canManageTag,
        followerRequirements,
        followerTierOptions,
        JoinedAt,
        profile,
        SearchablePlatforms,
        submissionUuid,
        TopRightComponent,
    }
    if (isVerifyMode) {
        return <VerifyLayout {...layoutProps} />
    }

    return <ReviewLayout {...layoutProps} />
}

export default Profile
